import React, { useId } from 'react'

// Shared icon set for the header, wallet popover, cashier and sidebar.
// Line icons use currentColor so their colour comes from CSS; currency icons carry their own colours.

type IconProps = { size?: number; className?: string }

const Line = ({ size = 24, className, children }: IconProps & { children: React.ReactNode }) => (
	<svg
		className={className}
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth={1.8}
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{children}
	</svg>
)

// useId() returns ids like ":r1:"; colons are awkward inside url(#…) references
const useSvgId = () => useId().replace(/:/g, '')

export const WalletIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M19 7V5.5A1.5 1.5 0 0 0 17.5 4H5a2 2 0 0 0 0 4h14a1 1 0 0 1 1 1v3" />
		<path d="M3 6v12a2 2 0 0 0 2 2h14a1 1 0 0 0 1-1v-3" />
		<path d="M21 12v4h-4a2 2 0 0 1 0-4h4z" />
	</Line>
)

export const CloseIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M6 6l12 12M18 6L6 18" />
	</Line>
)

export const ChevronDownIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M6 9l6 6 6-6" />
	</Line>
)

export const ChevronRightIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M9 6l6 6-6 6" />
	</Line>
)

export const ArrowRightIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M5 12h14M13 6l6 6-6 6" />
	</Line>
)

export const ArrowUpRightIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M14 4h6v6M20 4l-9 9" />
		<path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
	</Line>
)

export const PlusCircleIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 8v8M8 12h8" />
	</Line>
)

export const InfoIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 11v5M12 7.5v.01" />
	</Line>
)

export const GiftIcon = (p: IconProps) => (
	<Line {...p}>
		<rect x="3" y="8" width="18" height="4" rx="1" />
		<path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8M12 8v13" />
		<path d="M12 8S10.5 3 7.5 3a2.5 2.5 0 0 0 0 5M12 8s1.5-5 4.5-5a2.5 2.5 0 0 1 0 5" />
	</Line>
)

export const CashbackIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M4 12a8 8 0 1 0 2.5-5.8" />
		<path d="M4 4v4h4" />
	</Line>
)

export const EnvelopeIcon = (p: IconProps) => (
	<Line {...p}>
		<rect x="3" y="5" width="18" height="14" rx="3" />
		<path d="M4 7l8 6 8-6" />
	</Line>
)

export const PencilIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4z" />
		<path d="M13.5 6.5l4 4" />
	</Line>
)

export const UserIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="9.5" />
		<circle cx="12" cy="10" r="3.2" />
		<path d="M6 18.5c1.4-2.2 3.5-3.3 6-3.3s4.6 1.1 6 3.3" />
	</Line>
)

export const LogoutIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
		<path d="M10 8l-4 4 4 4M6 12h10" />
	</Line>
)

export const BankIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M3 9l9-5 9 5H3z" />
		<path d="M5 9v8M9.5 9v8M14.5 9v8M19 9v8M3 20h18M4 17h16" />
	</Line>
)

// The disc takes currentColor; the tick colour is passed in so it can contrast with it
export const CheckCircleIcon = ({ size = 22, className, tick = '#fff' }: IconProps & { tick?: string }) => (
	<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
		<circle cx="12" cy="12" r="11" fill="currentColor" />
		<path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke={tick} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
	</svg>
)

export const CrownIcon = ({ size = 24, className }: IconProps) => (
	<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
		<path d="M3 8l4.5 4L12 5l4.5 7L21 8l-1.8 10H4.8L3 8z" fill="currentColor" />
		<rect x="4.8" y="19" width="14.4" height="2" rx="1" fill="currentColor" />
	</svg>
)

/* Currency icons */

export const PointsCoinIcon = ({ size = 24, className }: IconProps) => {
	const id = useSvgId()
	return (
		<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
			<defs>
				<linearGradient id={`${id}o`} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#FFE27A" />
					<stop offset="1" stopColor="#E08A12" />
				</linearGradient>
				<linearGradient id={`${id}i`} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#F7B52C" />
					<stop offset="1" stopColor="#FFD54F" />
				</linearGradient>
			</defs>
			<circle cx="12" cy="12" r="11" fill={`url(#${id}o)`} />
			<circle cx="12" cy="12" r="8.2" fill={`url(#${id}i)`} stroke="#C97A0C" strokeWidth=".8" />
			<path
				d="M15.4 9.2A4 4 0 1 0 16 13h-3.6"
				fill="none"
				stroke="#A45A06"
				strokeWidth="2.2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	)
}

export const TicketIcon = ({ size = 24, className }: IconProps) => {
	const id = useSvgId()
	return (
		<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
			<defs>
				<linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0" stopColor="#FF6B8A" />
					<stop offset="1" stopColor="#E0193F" />
				</linearGradient>
			</defs>
			<g transform="rotate(-20 12 12)">
				<path
					d="M3 7.5A1.5 1.5 0 0 1 4.5 6h15A1.5 1.5 0 0 1 21 7.5v2a2.5 2.5 0 0 0 0 5v2a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 16.5v-2a2.5 2.5 0 0 0 0-5v-2z"
					fill={`url(#${id})`}
				/>
				<path d="M15 7.5v9" stroke="#FFC2CF" strokeWidth="1" strokeDasharray="1.5 1.5" />
			</g>
		</svg>
	)
}

export const GemIcon = ({ size = 24, className }: IconProps) => {
	const id = useSvgId()
	return (
		<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
			<defs>
				<linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#E9B6FF" />
					<stop offset="1" stopColor="#7B2FE0" />
				</linearGradient>
			</defs>
			<path d="M12 2l4 6-4 14-4-14 4-6z" fill={`url(#${id})`} />
			<path d="M7 7l1 3-1 9-4-5 1-6 3-1z" fill="#9B4DF0" />
			<path d="M17 7l-1 3 1 9 4-5-1-6-3-1z" fill="#6A22C9" />
			<path d="M12 2l-1.6 6h3.2L12 2z" fill="#F6DDFF" opacity=".8" />
		</svg>
	)
}

export const DiamondIcon = ({ size = 24, className }: IconProps) => {
	const id = useSvgId()
	return (
		<svg className={className} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
			<defs>
				<linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" stopColor="#8FE3FF" />
					<stop offset="1" stopColor="#1E6FE8" />
				</linearGradient>
			</defs>
			<path d="M6.5 4h11L22 9.5 12 21 2 9.5 6.5 4z" fill={`url(#${id})`} />
			<path d="M2 9.5h20M8 9.5L12 21l4-11.5M6.5 4L8 9.5 12 4l4 5.5L17.5 4" fill="none" stroke="#D8F6FF" strokeWidth=".9" strokeLinejoin="round" opacity=".85" />
		</svg>
	)
}

/* Payment brand marks (simplified) */

export const MastercardMark = ({ className }: { className?: string }) => (
	<svg className={className} width="40" height="26" viewBox="0 0 40 26" aria-hidden="true">
		<circle cx="13" cy="13" r="12" fill="#EB001B" />
		<circle cx="27" cy="13" r="12" fill="#F79E1B" />
		<path d="M20 3.3a12 12 0 0 1 0 19.4 12 12 0 0 1 0-19.4z" fill="#FF5F00" />
	</svg>
)

export const VisaMark = ({ className }: { className?: string }) => (
	<svg className={className} width="54" height="20" viewBox="0 0 54 20" aria-hidden="true">
		<text
			x="27"
			y="17"
			textAnchor="middle"
			fontFamily="Arial Black, Arial, sans-serif"
			fontSize="20"
			fontStyle="italic"
			fontWeight="900"
			fill="#fff"
			letterSpacing="-0.5"
		>
			VISA
		</text>
		<path d="M3 4.5L6 2h4L7 4.5z" fill="#F7B600" />
	</svg>
)

export const WiseMark = ({ className }: { className?: string }) => (
	<svg className={className} width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
		<path d="M4 5h16l-6.2 7.2H18L8.6 21l2.6-7.2H5.8L9 9.6H4z" fill="#9FE870" />
	</svg>
)

/* Sidebar menu icons */

export const MissionIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="8.5" />
		<circle cx="12" cy="12" r="4.5" />
		<circle cx="12" cy="12" r=".8" fill="currentColor" />
	</Line>
)

export const SwordsIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
		<path d="M13 19l6-6M16 16l4 4M19 21l2-2" />
		<path d="M14.5 6.5L18 3h3v3l-3.5 3.5M5 14l4 4M7 17l-3 3M3 19l2 2" />
	</Line>
)

export const LeaderboardIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M8 21V11H3v10h5zM15.5 21V5h-7v16h7zM21 21v-7h-5.5v7H21z" />
	</Line>
)

export const GamepadIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M6.5 7h11a4 4 0 0 1 3.9 4.8l-1 5A2.6 2.6 0 0 1 16 18l-1.8-2H9.8L8 18a2.6 2.6 0 0 1-4.4-1.2l-1-5A4 4 0 0 1 6.5 7z" />
		<path d="M7.5 10.5v3M6 12h3M15.5 11h.01M17.5 13h.01" />
	</Line>
)

export const LevelsIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4l-5.2 2.7 1-5.8L3.5 9.2l5.9-.9L12 3z" />
	</Line>
)

export const StoreIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M4 8l1.5-4h13L20 8M4 8v1.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0V8H4z" />
		<path d="M5 12v8h14v-8M10 20v-5h4v5" />
	</Line>
)

export const QuizIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="9" />
		<path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.4M12 17h.01" />
	</Line>
)

export const BallIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 7.5l4.2 3-1.6 5h-5.2l-1.6-5L12 7.5zM12 3v4.5M21 10.5l-4.8 0M3 10.5h4.8M7 20l2.4-4.5M17 20l-2.4-4.5" />
	</Line>
)

export const PrizeDropIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M7 9a5 5 0 0 1 10 0M7 9h10M7 9l5 6 5-6" />
		<rect x="9" y="15" width="6" height="5" rx="1" />
	</Line>
)

export const JackpotIcon = (p: IconProps) => (
	<Line {...p}>
		<ellipse cx="12" cy="6" rx="7" ry="3" />
		<path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" />
	</Line>
)

export const RaffleIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M3 8a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a2 2 0 0 0 0 4v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a2 2 0 0 0 0-4V8z" />
		<path d="M15 7v2M15 11v2M15 15v2" />
	</Line>
)

export const CasinoIcon = (p: IconProps) => (
	<Line {...p}>
		<rect x="3.5" y="3.5" width="17" height="17" rx="4" />
		<circle cx="8.5" cy="8.5" r="1" fill="currentColor" />
		<circle cx="15.5" cy="15.5" r="1" fill="currentColor" />
		<circle cx="12" cy="12" r="1" fill="currentColor" />
		<circle cx="15.5" cy="8.5" r="1" fill="currentColor" />
		<circle cx="8.5" cy="15.5" r="1" fill="currentColor" />
	</Line>
)

export const LoyaltyIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="12" cy="9" r="5.5" />
		<path d="M8.5 13.5L7 21l5-2.5 5 2.5-1.5-7.5" />
	</Line>
)

export const LotteryIcon = (p: IconProps) => (
	<Line {...p}>
		<circle cx="8" cy="9" r="4" />
		<circle cx="16" cy="9" r="4" />
		<circle cx="12" cy="16" r="4" />
	</Line>
)

export const ResetIcon = (p: IconProps) => (
	<Line {...p}>
		<path d="M4 12a8 8 0 1 0 2.5-5.8" />
		<path d="M4 4v4h4" />
	</Line>
)
